import React, { useMemo, useState } from 'react';
import { Div, Heading } from '@kizi/core-components';
import { IconV2 as Icon } from '@kizi/shared-components';

import {
  ExperienceCollectiveCardProps,
  ExperienceCollectiveCard,
} from './ExperienceCollectiveCard';

interface CarousellDotsProps {
  totalSwipes: number;
  activeDot: number;
  handleClick: (value: number) => void;
}

export const CarousellDots: React.FC<CarousellDotsProps> = ({
  totalSwipes,
  activeDot,
  handleClick,
}) => {
  const dotsArr = new Array(totalSwipes).fill(null);

  return (
    <>
      {dotsArr.map((_, i) => (
        <Div
          ml={2}
          width={8}
          height={8}
          borderRadius={3}
          border="2px solid"
          borderColor="main.1"
          display="inline-block"
          key={i}
          bg={activeDot === i ? 'main.1' : 'none'}
          onClick={() => {
            handleClick(i);
          }}
          css={{
            cursor: 'pointer',
          }}
        />
      ))}
    </>
  );
};

export interface ExperienceCollectiveCarouselProps {
  title: string;
  elementsPerSwipe?: number;
  reviews: ExperienceCollectiveCardProps[];
}

export const sliceIntoChunks = (arr, chunkSize: number) => {
  const res = [] as any;
  for (let i = 0; i < arr.length; i += chunkSize) {
    const chunk = arr.slice(i, i + chunkSize);
    res.push(chunk);
  }
  return res;
};

export const ExperienceCollectiveCarousel: React.FC<ExperienceCollectiveCarouselProps> =
  ({ title, reviews, elementsPerSwipe = 3 }) => {
    const totalSwipes = Math.ceil(reviews.length / elementsPerSwipe);
    const [activeDot, setActiveDot] = useState(0);

    const arrChunks = useMemo(
      () => sliceIntoChunks(reviews, elementsPerSwipe),
      [reviews]
    );

    const swipeLeft = () => {
      activeDot !== 0
        ? setActiveDot(activeDot - 1)
        : setActiveDot(totalSwipes - 1);
    };

    const swipeRight = () => {
      activeDot < totalSwipes - 1
        ? setActiveDot(activeDot + 1)
        : setActiveDot(0);
    };
    return (
      <Div bg="second.2" p={7} position="relative">
        <Heading variant="H2" as="h2" textAlign="center">
          {title}
        </Heading>

        <Div
          display="flex"
          alignItems="strech"
          justifyContent="center"
          css={{ gap: 30 }}
          minHeight="330px"
        >
          <Icon
            name="big-arrow-left"
            fill="main.1"
            position="absolute"
            top="50%"
            left="6px"
            size={44}
            cursor="pointer"
            onClick={swipeLeft}
            data-testid="arrow-left"
          />
          {arrChunks[activeDot].map(({ name, text, date }) => (
            <ExperienceCollectiveCard name={name} text={text} date={date} />
          ))}
          <Icon
            name="big-arrow-right"
            fill="main.1"
            position="absolute"
            top="50%"
            right="6px"
            size={35}
            cursor="pointer"
            onClick={swipeRight}
            data-testid="arrow-right"
          />
        </Div>

        <Div textAlign="center" mt={7}>
          <CarousellDots
            totalSwipes={totalSwipes}
            activeDot={activeDot}
            handleClick={setActiveDot}
          />
        </Div>
      </Div>
    );
  };
